/**
 * Canonical Schema Provider - Decouples canonical schema access from persistence.
 *
 * Breaks the circular dependency that would arise if projectStore imported schemaBridge
 * (which already imports projectStore). schemaBridge registers the provider once at
 * module load. projectStore and persistenceBridge read from it during save without
 * knowing anything about schemaBridge internals.
 *
 * Dependency graph:
 *   schemaBridge → canonicalProvider ← projectStore
 *   schemaBridge → projectStore           (existing, unchanged)
 *   canonicalProvider → nuvraSchema       (type import only)
 *
 * No circular imports.
 */

import type { NuvraSchema } from "./nuvraSchema";

let _provider: (() => NuvraSchema | null) | null = null;

/**
 * Register the canonical schema provider function.
 * Called once by schemaBridge at module load.
 * Calling again overwrites the previous provider (for test isolation).
 */
export function registerCanonicalProvider(fn: () => NuvraSchema | null): void {
  _provider = fn;
}

/**
 * Get the current canonical schema.
 * Returns null if the provider has not been registered or the schema is not initialized.
 * Never throws - null is a valid "not ready" state.
 */
export function getCanonicalSchema(): NuvraSchema | null {
  return _provider ? _provider() : null;
}
